import { DateRangeSpecialValue } from "@components/inputs/date/popup/Calendar.utils";
import { isSameDay } from "@components/inputs/date/utils";
import { SimpleTimeLineStatus } from "@components/simpleTimeline/SimpleTimeline.utils";
import {
    getInfoValue,
    ifAny,
    IFieldDef,
    IGetValueArgs,
    isVisibleByPath,
    ITemporalPropertySettings,
    TGetValueFn,
    TTemporalDialogSettings,
    TValidatorFn
} from "@components/smart/FieldInfo";
// keep exact path because of circular dependencies
import {
    ActionType,
    ISmartFastEntriesActionEvent
} from "@components/smart/smartFastEntryList/SmartFastEntryList.utils";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { TemporalEntityEntity } from "@odata/GeneratedEntityTypes";
import { getEnumDisplayValue, getEnumEntities, getEnumSelectItems, isEnum } from "@odata/GeneratedEnums.utils";
import { TFieldDefinition, TFieldsDefinition } from "@pages/PageUtils";
import {
    areValuesEqual,
    forEachKey,
    isDefined,
    isNotDefined,
    isObjectEmpty,
    isTwoPrimitiveArraysEqual
} from "@utils/general";
import { Dayjs } from "dayjs";
import { cloneDeep } from "lodash";
import React from "react";
import { ValidationError } from "yup";

import SmartTemporalPropertyButton from "../components/smart/smartTemporalPropertyButton/SmartTemporalPropertyButton";
import { ValidatorType } from "../enums";
import { TRecordAny, TValue } from "../global.types";
import { Model } from "../model/Model";
import { DATE_MAX, DATE_MIN, getUtcDayjs, TDateLike } from "../types/Date";
import memoize from "../utils/memoize";
import { FormStorage, IFormStorageDefaultCustomData } from "../views/formView/FormStorage";
import BindingContext, { createPath, ENUM_KEY_PROP_NAME, IEntity, SEPARATOR } from "./BindingContext";
import { updateDataOnChange } from "./Data.utils";
import { PrEntityValueSourceCode } from "./GeneratedEnums";

export type TTemporalFormStorage = FormStorage<IEntity, ISmartTemporalPropertyDialogCustomData>;

export const SourceCodeLocalPath = "##SourceCode##";
export const IsItemWithRemovedSyncPath = "##IsItemWithRemovedSync##";
export const systemProperties = ["Id", BindingContext.NEW_ENTITY_ID_PROP, "DateCreated", "CreatedBy", "DateLastModified", "LastModifiedBy", "TemporaryGuid", SourceCodeLocalPath];

export enum TemporalEntityProp {
    CurrentTemporalPropertyBag = "CurrentTemporalPropertyBag",
    TemporalPropertyBag = "TemporalPropertyBag",
    Default = "Default" // navigation to default entity of synchronized collections
}

export type TTemporal<T = Record<string, unknown>> = T & {
    DateValidFrom: Date;
    DateValidTo: Date;
    // FE only flag to distinguish extracted history items in temporalPropertyDialog
    [SourceCodeLocalPath]?: PrEntityValueSourceCode;
}

export type TTemporalLineItem<T = Record<string, unknown>> = TTemporal<{
    [TemporalEntityProp.CurrentTemporalPropertyBag]: T;
    [TemporalEntityProp.TemporalPropertyBag]: TTemporal<T>[];
    Default?: IEntity; // default value for synchronized ranges
}>

export interface ISmartTemporalPropertyDialogCustomData extends IFormStorageDefaultCustomData {
    // bc for which the current SmartTemporalPropertyDialog is opened,
    // needed for callbacks used for DateValidFrom/DateValidTo,
    // because those fields have one definition shared across all the dialogs,
    // but we need to be able to target those callback for a specific one
    openedDialogBindingContext?: BindingContext;
    dialogSettings?: TTemporalDialogSettings;
    dateBeforeDecisiveDateMsg?: string;
}

interface IDialogSettingsArgs {
    dialogSettings: TTemporalDialogSettings;
}

interface IBindingContextArgs {
    bindingContext: BindingContext;
}

/**
 * Returns the date that should be used for distinguish history values - mostly lastWageDate, but could be different
 * as TemporalDialog is common component.
 */
export function getDecisiveDateForHistoryValues(storage: TTemporalFormStorage, args: IDialogSettingsArgs | IBindingContextArgs): Dayjs {
    let dialogSettings: TTemporalDialogSettings = null;
    if (args.hasOwnProperty("dialogSettings")) {
        dialogSettings = (args as IDialogSettingsArgs).dialogSettings;
    } else {
        const bindingContext = (args as IBindingContextArgs).bindingContext;
        dialogSettings = storage.getTemporalDialogSetting(bindingContext);
    }
    if (!dialogSettings) {
        // [This is here just to be sure that we don't have any unexpected behavior, because this should never happen,]
        // [as TemporalDialog is always opened with dialogSettings. If it happens, it means that we are trying to call]
        // [getDecisiveDateForHistoryValues -> fix the call and pass correct args with related dialog settings or BC.]
        //  EDIT: some fields are maintained "manually", e.g. Deductions->IsIncludingAllEmployees, so
        //  they don't have any special settings. All the props in dialogSettings are optional anyway, so we have
        //  to be able to live without it at all. >>> Lower the exception to console warning...
        console.warn("Dialog settings are not defined. Decisive date cannot be determined.");
    }
    // if storage is not set, we consider the entity as a new one -> use DATE_MIN as decisive date for history values
    let decisiveDate = (!storage || storage?.data.bindingContext.isNew()) ? getUtcDayjs(DATE_MIN)
        : ((dialogSettings && getInfoValue(dialogSettings, "decisiveDateForHistoryValues", { storage })) ?? storage?.data.customData.lastWageDate);
    if (decisiveDate instanceof Date) {
        // make DayJS object if Date is returned from callback
        decisiveDate = getUtcDayjs(decisiveDate);
    }
    return decisiveDate;
}

export function groupTemporalRanges<T, K extends keyof T>(propertyBags: TTemporal<T>[], properties: K[], withoutValidations = false)
    : TTemporal<Pick<T, K> & { [SourceCodeLocalPath]?: string }>[] {
    if (propertyBags.length === 0) {
        return [];
    }

    const bags = [...propertyBags];
    const result: TTemporal<Pick<T, K> & { [SourceCodeLocalPath]?: string }>[] = [];

    bags.sort((a, b) => a.DateValidFrom.getTime() - b.DateValidFrom.getTime());

    if (!withoutValidations) {
        validateRanges(bags);
    }

    // Add sourceCode, so it is copied and also don't merge rows without different value of it
    const updatedProperties: (keyof T)[] = [...properties];

    if (!updatedProperties.includes(SourceCodeLocalPath as keyof T)) {
        updatedProperties.push(SourceCodeLocalPath as keyof T);
    }

    let bag = bags[0];
    let currentProperties = getProperties(bag, updatedProperties);
    let currentFrom = bag.DateValidFrom, currentTo = bag.DateValidTo;
    const addResult = () => {
        result.push({
            DateValidFrom: currentFrom,
            DateValidTo: currentTo,
            ...currentProperties
        });
    };

    for (let i = 1; i < bags.length; i++) {
        bag = bags[i];
        const props = getProperties(bag, updatedProperties);

        if (anyPropsChanged(updatedProperties, currentProperties, props) || !areOneDayApart(bag.DateValidFrom, bags[i - 1].DateValidTo)) {
            addResult();
            currentProperties = props;
            currentFrom = bag.DateValidFrom;
            currentTo = bag.DateValidTo;
        } else {
            currentTo = bag.DateValidTo;
        }
    }

    if (!isObjectEmpty(currentProperties)) {
        addResult();
    }

    return result;
}

export function ungroupTemporalRanges<T>(groupedRanges: TTemporal<T>[], originalPropertyBags: TTemporal<T>[], properties: string[]): TTemporal<Partial<T>>[] {
    groupedRanges = [...groupedRanges];
    const originalPropertyBagsCopy = originalPropertyBags ? [...originalPropertyBags] : [];
    groupedRanges.sort((a, b) => a.DateValidFrom.getTime() - b.DateValidFrom.getTime());
    originalPropertyBagsCopy.sort((a, b) => a.DateValidFrom.getTime() - b.DateValidFrom.getTime());
    const bagsStack = [...originalPropertyBagsCopy];
    validateRanges(groupedRanges);
    validateRanges(originalPropertyBagsCopy);
    const result: TTemporal<Partial<T>>[] = [];

    while (groupedRanges.length > 0) {
        let currentGroup = { ...groupedRanges.shift() };
        let groupFrom = getUtcDayjs(currentGroup.DateValidFrom);
        const groupTo = getUtcDayjs(currentGroup.DateValidTo);

        if (bagsStack?.length === 0) {
            result.push(Object.assign({}, currentGroup));
        } else {
            while (bagsStack.length > 0 && currentGroup !== null) {
                const currentBag = { ...bagsStack.shift() };
                const bagFrom = getUtcDayjs(currentBag.DateValidFrom);
                const bagTo = getUtcDayjs(currentBag.DateValidTo);

                if (bagTo.isBefore(groupFrom, "day")) {
                    const obj = Object.assign({}, currentBag);
                    setProperties(obj, {}, properties);
                    result.push(obj);
                    continue;
                }
                if (bagFrom.isSame(groupFrom, "day") && bagTo.isSame(groupTo, "day")) {
                    const obj = Object.assign({}, currentBag);
                    setProperties(obj, currentGroup, properties);
                    result.push(obj);
                    currentGroup = null;
                    break;
                }
                if (bagFrom.isBefore(groupFrom, "day")) {
                    const obj = Object.assign({}, currentBag);
                    const newBag = Object.assign({}, currentBag);
                    setProperties(obj, {}, properties);
                    obj.DateValidTo = groupFrom.subtract(1, "day").toDate();
                    removeSystemProperties(newBag);
                    newBag.DateValidFrom = groupFrom.toDate();
                    bagsStack.unshift(newBag);
                    result.push(obj);
                    continue;
                }
                if (groupFrom.isBefore(bagFrom, "day")) {
                    bagsStack.unshift(currentBag);
                    const obj: TTemporal = {
                        DateValidFrom: groupFrom.toDate(),
                        DateValidTo: bagFrom.subtract(1, "day").toDate()
                    };
                    setProperties(obj, currentGroup, properties);
                    if (groupTo.isBefore(bagFrom, "day")) {
                        obj.DateValidTo = groupTo.toDate();
                        currentGroup = null;
                    } else {
                        groupFrom = bagFrom;
                        currentGroup.DateValidFrom = groupFrom.toDate();
                    }
                    result.push(obj as TTemporal<Partial<T>>);
                    continue;
                }
                //Now groupFrom === bagFrom
                if (bagTo.isBefore(groupTo, "day")) {
                    const obj = Object.assign({}, currentBag);
                    setProperties(obj, currentGroup, properties);
                    groupFrom = bagTo.add(1, "day");
                    currentGroup.DateValidFrom = groupFrom.toDate();
                    if (!bagsStack.length) {
                        // if there are no more bags, correct also DateValidTo of obj from current group
                        obj.DateValidTo = groupTo.toDate();
                    }
                    result.push(obj);
                    continue;
                }
                if (groupTo.isBefore(bagTo)) {
                    const obj = Object.assign({}, currentBag);
                    const newBag = Object.assign({}, currentBag);
                    removeSystemProperties(newBag);
                    newBag.DateValidFrom = groupTo.add(1, "day").toDate();
                    setProperties(obj, currentGroup, properties);
                    obj.DateValidTo = groupTo.toDate();
                    currentGroup = null;
                    bagsStack.unshift(newBag);
                    result.push(obj);
                    continue;
                }
                throw new Error("Combination I didn't think of.");
            }
        }
    }

    while (bagsStack.length > 0) {
        const currentBag = bagsStack.shift();
        const obj = Object.assign({}, currentBag);
        setProperties(obj, {}, properties);
        result.push(obj);
    }

    return result;
}

export function optimizeRanges(ranges: TTemporal[]): void {
    removeEmptyRanges(ranges);
    mergeSameRanges(ranges);
}

export function markSynchronizedRanges(ranges: TTemporal[], properties: (keyof TTemporal)[]): TTemporal[] {
    return ranges?.map(range => ({
        ...range,
        [SourceCodeLocalPath]: isEmptyRange(getProperties(range, properties)) ? PrEntityValueSourceCode.Default : null
    }));
}

export function groupAndOptimizeRanges<T>(propertyBags: TTemporal<T>[], properties: (keyof T)[], withoutValidations = false): TTemporal[] {
    const result: TTemporal[] = groupTemporalRanges(propertyBags ?? [], properties, withoutValidations);

    optimizeRanges(result);
    return result;
}

export function ungroupAndOptimizeRanges(groupedRanges: TTemporal[], originalPropertyBags: TTemporal[], properties: string[]): TTemporal[] {
    const result: TTemporal[] = ungroupTemporalRanges(groupedRanges, originalPropertyBags, properties);

    optimizeRanges(result);
    return result;
}

export function mergeSameRanges(ranges: any[]): void {
    let pos = 1;
    while (pos < ranges.length) {
        if (hasSameProperties(ranges[pos - 1], ranges[pos]) && areOneDayApart(ranges[pos - 1].DateValidTo, ranges[pos].DateValidFrom)) {
            let removePos;
            if (!Number.isInteger(ranges[pos - 1].Id)) {
                removePos = pos - 1;
                ranges[pos].DateValidFrom = ranges[removePos].DateValidFrom;
            } else {
                removePos = pos;
                ranges[pos - 1].DateValidTo = ranges[pos].DateValidTo;
            }
            ranges.splice(removePos, 1);
        } else {
            pos++;
        }
    }
}

function areOneDayApart(date1: Date, date2: Date) {
    return Math.abs(getUtcDayjs(date1).startOf("day").diff(getUtcDayjs(date2).startOf("day"), "day")) <= 1;
}

function removeEmptyRanges(ranges: any[]) {
    let pos = 0;
    while (pos < ranges.length) {
        if (isEmptyRange(ranges[pos]) && !isSynchronizedRange(ranges[pos])) {
            ranges.splice(pos, 1);
        } else {
            pos++;
        }
    }
}

function hasSameProperties(range1: any, range2: any) {
    // check that the objects has same properties
    // if one has keys that the other doesn't it wouldn't be caught by the Object.keys(range1) check
    const range1Keys = Object.keys(range1).sort().filter(prop => !isSystemProperty(prop));
    const range2Keys = Object.keys(range2).sort().filter(prop => !isSystemProperty(prop));

    if (!isTwoPrimitiveArraysEqual(range1Keys, range2Keys)) {
        return false;
    }

    for (const property of Object.keys(range1)) {
        if (!isPropertySame(range1, range2, property)) {
            return false;
        }
    }
    return true;
}

function isPropertySame(range1: any, range2: any, property: string) {
    if (isSystemProperty(property)) {
        return true;
    }

    const val1 = range1[property];
    const val2 = range2[property];

    return areValuesEqual(val1, val2);
}

function isEmptyRange(range: any): boolean {
    for (const property of Object.keys(range)) {
        if (!isPropertyEmpty(range, property)) {
            return false;
        }
    }
    return true;
}

function isSynchronizedRange(range: TTemporal) {
    return (range[SourceCodeLocalPath] && range[SourceCodeLocalPath] !== PrEntityValueSourceCode.Entity);
}

function isPropertyEmpty(range: any, property: string) {
    return isSystemProperty(property) || isNotDefined(range[property]) ||
        (typeof range[property] === "object" && isObjectEmpty(range[property]));
}

function isSystemProperty(property: string) {
    return systemProperties.includes(property) || property === "DateValidFrom" || property === "DateValidTo";
}

function removeSystemProperties(obj: IEntity) {
    for (const p of systemProperties) {
        delete obj[p];
    }
}

function setProperties(obj: any, from: any, properties: string[]) {
    for (const p of properties) {
        if (!isSystemProperty(p)) {
            if (from[p] !== undefined) {
                obj[p] = from[p];
            } else {
                delete obj[p];
            }
        }

    }
}

export function validateRanges(groupedRanges: TTemporal[]): void {
    for (const group of groupedRanges) {
        if (getUtcDayjs(group.DateValidFrom).isAfter(group.DateValidTo, "day")) {
            throw new Error("From must be less then to.");
        }
    }
    for (let pos = 1; pos < groupedRanges.length; pos++) {
        if (getUtcDayjs(groupedRanges[pos - 1].DateValidTo).isSameOrAfter(groupedRanges[pos].DateValidFrom, "day")) {
            throw new Error("Intervals cannot overlap.");
        }
    }
}

function anyPropsChanged<T>(properties: (keyof T)[], bag1: Partial<T>, bag2: Partial<T>): boolean {
    for (const p of properties) {
        if (!isPropertySame(bag1, bag2, p.toString())) {
            return true;
        }
    }
    return false;
}

function getProperties<T, K extends keyof T>(bag: TTemporal<T>, properties: K[]): TTemporal<Pick<T, K>> {
    const result: Partial<TTemporal<Pick<T, K>>> = {};
    for (const p of properties) {
        if (bag[p] !== undefined) {
            result[p] = bag[p];
        }
    }

    return result as TTemporal<Pick<T, K>>;
}

export const getGroupingProperties = memoize((bindingContext: BindingContext, storage: FormStorage) => {
    const properties = new Set<string>();
    const codeProperty = bindingContext.isEnum() ? bindingContext.getProperty()?.getReferentialConstraint()?.property : null;

    properties.add(bindingContext.getPath());

    // for enums, add code property so that we have both the navigation object and code string correctly updated when grouping/ungrouping
    if (codeProperty) {
        properties.add(codeProperty);
    }

    // multiple fields can be grouped into one temporal multi property
    const dialogSettingsColumns = storage.getInfo(bindingContext)?.fieldSettings?.temporalDialog?.columns;

    if (dialogSettingsColumns) {
        for (const column of dialogSettingsColumns) {
            properties.add(column);

            const colBc = bindingContext.getParent().navigate(column);

            // add Code property for enum navigations
            if (colBc.isEnum()) {
                const codeProperty = colBc.getProperty()?.getReferentialConstraint()?.property;

                properties.add(codeProperty);
            }
        }
    }

    return Array.from(properties);
}, (bc: BindingContext) => {
    return bc.getFullPath();
});

export const setTemporalRangeNewValue = (storage: FormStorage, e: ISmartFieldChange, range: TTemporal): TTemporal => {
    const bc = e.bindingContext;
    let updatedRange = { ...range };

    if (bc.isEnum()) {
        if (e.value) {
            const data: TRecordAny = {};
            Object.keys(e.additionalData ?? {}).forEach(key => {
                if (Object.hasOwn(e.additionalData, key) && bc.isValidNavigation(key)) {
                    data[key] = e.additionalData[key];
                }
            });
            updatedRange[bc.getPath()] = isObjectEmpty(data)
                ? getEnumEntities(bc.getEntityType().getName()).find(entity => entity[ENUM_KEY_PROP_NAME] === e.value)
                : data;
        }
        updatedRange[bc.getProperty().getReferentialConstraint().property] = e.value;
    } else {
        updatedRange = updateDataOnChange({
            storage,
            dataBindingContext: bc.getParent(),
            bindingContext: bc,
            data: range,
            newValue: e.value,
            preventCloning: true
        });
    }

    return updatedRange;
};

function getDialogSettings(storage: Model): TTemporalDialogSettings {
    const { dialogSettings } = (storage as FormStorage<unknown, ISmartTemporalPropertyDialogCustomData>).getCustomData() ?? {};
    return dialogSettings ?? {};
}

export const getTemporalPropertyExtraFieldContentAfter = (customTemporalNavigationPath?: string, customTemporalPropertyPath?: string): TGetValueFn<React.ReactElement> => {
    return (args: IGetValueArgs) => {
        // if (isFieldReadOnly(args.info, args.storage, args.bindingContext)) {
        //     return null;
        // }
        return (
            <SmartTemporalPropertyButton storage={args.storage as FormStorage}
                                         bindingContext={args.bindingContext}
                                         customTemporalNavigationPath={customTemporalNavigationPath}
                                         customTemporalPropertyPath={customTemporalPropertyPath}
            />
        );
    };
};

export const getTimelineStatus = (storage: FormStorage, temporalItem: TTemporal): SimpleTimeLineStatus => {
    const _decisiveDate = getDecisiveDateForHistoryValues(storage, { dialogSettings: (storage as TTemporalFormStorage).getCustomData().dialogSettings });
    return _decisiveDate?.isSame(DATE_MIN, "day") ? SimpleTimeLineStatus.Future /* Special case when decisiveDate is MIN_DATE we consider everything as future */
        : _decisiveDate?.isBetween(temporalItem.DateValidFrom, temporalItem.DateValidTo, "day", "[]")
            ? SimpleTimeLineStatus.Current
            : _decisiveDate?.isAfter(temporalItem.DateValidTo) ? SimpleTimeLineStatus.History : SimpleTimeLineStatus.Future;

};

const getTemporalItemTimeLineStatus = (storage: FormStorage, bindingContext: BindingContext, dataStore = storage.data.origEntity) => {
    // saved items that are in the past should no longer be editable
    const key = bindingContext.getKey();

    if (isNotDefined(key)) {
        return null;
    }

    const item = storage.getValue(bindingContext, { dataStore: dataStore });

    if (!item) {
        return null;
    }

    return getTimelineStatus(storage, item);
};

export const checkTemporalItemTimeLineStatus = (storage: FormStorage, bindingContext: BindingContext, status: SimpleTimeLineStatus[], dataStore?: TRecordAny): boolean => {
    return status.includes(getTemporalItemTimeLineStatus(storage, bindingContext, dataStore));
};

export const isTemporalFieldReadOnly: TGetValueFn<boolean> = (args: IGetValueArgs) => {
    const itemBc = args.bindingContext.getParent();
    const dialogSettings = getDialogSettings(args.storage);

    const row = args.storage.getValue(itemBc);
    if (row?.[SourceCodeLocalPath] && row[SourceCodeLocalPath] !== PrEntityValueSourceCode.Entity) {
        // synchronized values should not be editable at all
        return true; // !isEditableRangeDateField(row, args.storage as FormStorage, args.bindingContext);
    }
    const withoutGaps = getInfoValue(dialogSettings, "withoutGaps", { storage: args.storage, bindingContext: itemBc });
    if (args.bindingContext.getPath() === TemporalEntityEntity.DateValidFrom && withoutGaps && isNthItemInCollection({
        storage: args.storage,
        bindingContext: args.bindingContext,
        index: 0
    })) {
        return true;
    }

    const readOnlyStates = [SimpleTimeLineStatus.History];
    if (args.bindingContext.getPath() !== TemporalEntityEntity.DateValidTo) {
        readOnlyStates.push(SimpleTimeLineStatus.Current);
    }

    // todo: timeline status is read from by default from origEntity, which doesn't include unsaved
    //  changes and synchronized values, check also Id, which are replaced in SmartTemporalPropertyDialog init??
    return checkTemporalItemTimeLineStatus(args.storage as FormStorage, itemBc, readOnlyStates);
};

export const isTemporalLineItemRemovable: TGetValueFn<boolean> = (args: IGetValueArgs) => {
    return !checkTemporalItemTimeLineStatus(args.storage as FormStorage, args.bindingContext, [SimpleTimeLineStatus.History, SimpleTimeLineStatus.Current]);
};

export const dateValidFromValidator: TValidatorFn = (value: TValue, args: IGetValueArgs) => {
    const storage = args.storage as TTemporalFormStorage;
    const itemBc = args.bindingContext.getParent();
    const item = storage.getValue(itemBc);
    const items = storage.getValue(args.bindingContext.getParent().removeKey()) as TTemporal[];
    const itemIndex = items.findIndex(i => i === item);
    const previousItem = items[itemIndex - 1];
    const isNewEntity = ((itemBc.getParentCollection() ?? itemBc.getRootParent()).isNew()) || !!item[IsItemWithRemovedSyncPath]; // if sync is removed act as if it's new entity

    const dialogSettings = storage.getCustomData().dialogSettings;
    if (dialogSettings) {
        // we can only validate data against decisive date -> when temporal dialog is opened
        const _decisiveDate = getDecisiveDateForHistoryValues(storage, { dialogSettings });
        if (_decisiveDate?.isAfter(DATE_MIN) && !isNewEntity) { // new entity can have date valid from MIN_DATE even if it's in closed period, especially with when withoutGaps setting
            const isSavedHistoryOrCurrentItem = checkTemporalItemTimeLineStatus(storage, itemBc, [SimpleTimeLineStatus.History, SimpleTimeLineStatus.Current]);
            if (!isSavedHistoryOrCurrentItem && getUtcDayjs(value as TDateLike).isBefore(_decisiveDate)) {
                const message = storage.getCustomData().dateBeforeDecisiveDateMsg ?? storage.t("Components:TemporalProperty.ValidationDateBeforeDecisiveDate");
                return new ValidationError(message, false, args.bindingContext.getEntityPath(true));
            }
        }
    }

    if (!previousItem) {
        return true;
    }

    if (previousItem.DateValidTo && item.DateValidFrom && getUtcDayjs(item.DateValidFrom).isBefore(previousItem.DateValidTo, "date")) {
        return new ValidationError(storage.t("Components:TemporalProperty.ValidationDateFromAfterPreviousDateTo"), false, args.bindingContext.getEntityPath(true));
    }

    return true;
};

export const dateValidToValidator: TValidatorFn = (value: TValue, args: IGetValueArgs) => {
    const storage = args.storage as TTemporalFormStorage;
    const itemBc = args.bindingContext.getParent();
    const item = storage.getValue(itemBc) as TTemporal;
    const isNewEntity = ((itemBc.getParentCollection() ?? itemBc.getRootParent()).isNew()) || !!item[IsItemWithRemovedSyncPath]; // if sync is removed act as if it's new entity

    const dialogSettings = storage.getCustomData().dialogSettings;
    if (dialogSettings) {
        // we can only validate data against decisive date -> when temporal dialog is opened
        const _decisiveDate = getDecisiveDateForHistoryValues(storage, { dialogSettings });
        if (_decisiveDate?.isAfter(DATE_MIN) && !isNewEntity) {
            const isSavedHistoryItem = checkTemporalItemTimeLineStatus(storage, itemBc, [SimpleTimeLineStatus.History]);
            // subtract one day from decisive date, as it is first possible date to change, and you can change interval to end
            if (!isSavedHistoryItem && getUtcDayjs(value as TDateLike).isBefore(_decisiveDate.subtract(1, "day"))) {
                const message = storage.getCustomData().dateBeforeDecisiveDateMsg ?? storage.t("Components:TemporalProperty.ValidationDateBeforeDecisiveDate");
                return new ValidationError(message, false, args.bindingContext.getEntityPath(true));
            }
        }
    }

    // compare dates in the current item
    if (item.DateValidFrom && item.DateValidTo && getUtcDayjs(item.DateValidFrom).isAfter(item.DateValidTo, "date")) {
        return new ValidationError(storage.t("Components:TemporalProperty.ValidationDateFromBeforeDateTo"), false, args.bindingContext.getEntityPath(true));
    }

    return true;
};

interface isNthItemProps {
    storage: Model,
    bindingContext: BindingContext,
    index: number
}

// for now relies on sorted data, doesn't work with sorted collection eg. DocumentItems sorted by Order prop
const isNthItemInCollection = ({ storage, bindingContext, index }: isNthItemProps): boolean => {
    const key = bindingContext.getParentCollection().getKey();
    const collectionBc = bindingContext.getParentCollection().removeKey();
    const collection = storage.getValue(collectionBc);
    const itemIndex = index < 0 ? collection.length + index : index;
    const nthItem = collection?.length ? collection[itemIndex] : null;
    return isDefined(nthItem) && key === collectionBc.addKey(nthItem).getKey();
};

export const TemporalValidityFieldsDef: TFieldsDefinition = {
    "TemporalPropertyBag/DateValidFrom": {
        useForValidation: true,
        fieldSettings: {
            showSpecialValue: (args: IGetValueArgs) => {
                const isFirstItem = isNthItemInCollection({
                    storage: args.storage,
                    bindingContext: args.bindingContext,
                    index: 0
                });
                return isFirstItem ? DateRangeSpecialValue.WithoutStart : null;
            }
        },
        isReadOnly: isTemporalFieldReadOnly,
        validator: {
            type: ValidatorType.Date,
            settings: {
                customValidator: dateValidFromValidator
            }
        },
        customizationData: {
            useForCustomization: false
        }
    },
    "TemporalPropertyBag/DateValidTo": {
        useForValidation: true,
        fieldSettings: {
            showSpecialValue: (args: IGetValueArgs) => {
                const isLastItem = isNthItemInCollection({
                    storage: args.storage,
                    bindingContext: args.bindingContext,
                    index: -1
                });
                return isLastItem ? DateRangeSpecialValue.WithoutEnd : null;
            }
        },
        isReadOnly: isTemporalFieldReadOnly,
        validator: {
            type: ValidatorType.Date,
            settings: {
                customValidator: dateValidToValidator
            }
        },
        customizationData: {
            useForCustomization: false
        }
    }
};

export const getGranularity: ((dialogSettings: TTemporalDialogSettings) => TTemporalDialogSettings["granularity"]) = (dialogSettings) => {
    return dialogSettings?.granularity ?? "day";
};

export function getTemporalAdditionalProperties<T extends TRecordAny, E extends TRecordAny>(tempEntity: T, entity: E): IFieldDef[] {
    // we always have to load all TemporalProperties for the range (un)grouping to work properly
    // and so that we are able to show correct (un)filled icon next to temporary property fields
    const temporalProps = Object.keys(tempEntity)
        .filter(key => !systemProperties.includes(key))
        .map(key => ({ id: `${entity.TemporalPropertyBag}/${key}`, useForValidation: true }));
    return [...temporalProps, ...getTableTemporalAdditionalProperties(entity)];
}

// Additional properties of current temporal property bag (datevalidfrom, datevalidto) used in table view
// to determine if current values are former/current/future values
export function getTableTemporalAdditionalProperties<E extends TRecordAny>(entity: E): IFieldDef[] {
    return ["DateValidFrom", "DateValidTo"].map(key => ({ id: `${entity.CurrentTemporalPropertyBag}/${key}` }));
}

export const isCurrentBag = (today: Dayjs, bag: TTemporal): boolean => today.isBetween(bag.DateValidFrom, bag.DateValidTo, "day", "[]");
export const isHistoryBag = (today: Dayjs, bag: TTemporal): boolean => today.isAfter(bag.DateValidTo, "day");
export const isFutureBag = (today: Dayjs, bag: TTemporal): boolean => today.isBefore(bag.DateValidFrom, "day");

/**
 * Returns startOf the current or next period according to the granularity and given date
 * @param dialogSettings
 * @param date
 * @param nextOne if true, returns start of the next period
 */
export function enhanceDateWithGranularity(dialogSettings: TTemporalDialogSettings, date: TDateLike, nextOne = false): Dayjs {
    const granularity = getGranularity(dialogSettings);
    if (dialogSettings?.getCustomDateGranularity) {
        return dialogSettings.getCustomDateGranularity(getUtcDayjs(date), nextOne);
    } else {
        date = getUtcDayjs(date);
        if (nextOne) {
            date = date.add(granularity === "day" ? 2 : 1, granularity);
        }
        return date.startOf(granularity);
    }
}

export function getEndOfGranularity(dialogSettings: TTemporalDialogSettings, date: TDateLike): Dayjs {
    const startOfNextPeriod = enhanceDateWithGranularity(dialogSettings, date, true);
    return startOfNextPeriod.subtract(1, "day");
}

/**
 * Get default DateValidFrom for new temporal range item, depends on other entities. If it's first temporal row,
 * start with DATE_MIN or custom newDefaultDateValidFrom. If there are already items in the past, starts with current
 * granularity. If there are items in the future or current, starts one period after the last one.
 * @param dialogSettings
 * @param items
 * @param decisiveDate - last wage date, usually end of the current period, used to split current range if decisiveDate is in the middle of it
 * @param withoutGaps - if true, the first item will start with DATE_MIN, otherwise it will start with decisiveDate + 1 day
 */
export const getDefaultTemporalDateValidFrom = (dialogSettings: TTemporalDialogSettings, items: TTemporal[], decisiveDate?: Dayjs, withoutGaps?: boolean): Date => {
    let firstAvailableDate = getUtcDayjs(DATE_MIN);

    if (isDefined(decisiveDate) && !decisiveDate.isSame(DATE_MIN, "day") && !(withoutGaps && items?.length === 0)) {
        firstAvailableDate = getUtcDayjs(decisiveDate).add(1, "day").startOf("day");
    }

    let defaultDate = getUtcDayjs(items?.length ? null : (dialogSettings?.newDefaultDateValidFrom ?? firstAvailableDate));

    if (!!defaultDate && isSameDay(defaultDate, DATE_MIN)) {
        // there is an issue with DayJs startOf which return 1901-01-01 if DATE_MIN is used
        return DATE_MIN;
    }

    const lastRange = items?.length ? items[items.length - 1] : null;
    defaultDate = enhanceDateWithGranularity(dialogSettings, defaultDate);

    if (lastRange) {
        if (isDefined(decisiveDate) &&
            getUtcDayjs(lastRange.DateValidFrom).isSameOrBefore(decisiveDate, "day") &&
            getUtcDayjs(lastRange.DateValidTo).isSameOrAfter(decisiveDate, "day") &&
            !decisiveDate.isSame(DATE_MIN, "day")) {
            defaultDate = firstAvailableDate;
        }
        // if the last range start is more than 2 days in the past, we start with current granularity,
        // otherwise we get the next period according to the last range start
        // note: 2 days is because we need to end the last range at least one day
        //      before the new range starts and also one day after the last range start
        else if (defaultDate.isSameOrBefore(getUtcDayjs(lastRange.DateValidFrom).add(2, "day"), "day")) {
            defaultDate = enhanceDateWithGranularity(dialogSettings, lastRange.DateValidFrom, true);
        }
    }

    return defaultDate.toDate();
};

/**
 * Get default DateValidTo for new temporal range item, depends on other entities. If there are already future items,
 * returns the first day before the start of the next item,
 * @param dialogSettings
 * @param items
 */
export const getDefaultTemporalDateValidTo = (dialogSettings: TTemporalDialogSettings, items: TTemporal[]): Date => {
    return DATE_MAX;
};

/**
 * Method prepares current range in the list of temporal ranges. If exists, just returns the index and keeps the ranges.
 * If not exists, creates a new one and returns updated list of ranges
 */
export const getOrPrepareNewCurrentRange = (dialogSettings: TTemporalDialogSettings, ranges: TTemporal[], decisiveDate?: Dayjs, isChangeOnNewEntity = false): {
    ranges: TTemporal[],
    currentIndex: number
} => {
    let defaultDate = enhanceDateWithGranularity(dialogSettings, getUtcDayjs());
    if (decisiveDate && defaultDate.isBefore(decisiveDate)) {
        defaultDate = enhanceDateWithGranularity(dialogSettings, decisiveDate, true);
    }

    const currentRangeIndex = getCurrentRangeIndex(ranges, defaultDate.toDate());
    let currentRange = currentRangeIndex !== -1 ? ranges[currentRangeIndex] : null;

    // in case there is a planned change for tomorrow, we move the defaultDate to yesterday,
    // so we can also end the current range before the new one starts and "today" will be still in the current range
    const tomorrow = defaultDate.add(1, "day");
    const tomorrowRange = ranges.find(r => getUtcDayjs(r.DateValidFrom).isSame(tomorrow, "day"));
    if (tomorrowRange) {
        defaultDate = defaultDate.subtract(1, "day");
    }

    // in case current range already exists, return the index and keep the ranges as they are
    // note: we consider the current range as same even if it starts "yesterday", because
    //  we cannot end the range in same day it starts
    // const yesterday = defaultDate.subtract(1, "day");

    // must be before split, because we need to clone the current range before we change it
    const dataToCopy = cloneDeep(currentRange ?? {}) as TRecordAny;
    delete dataToCopy.Id;

    // handle split by decisiveDate if current range exists
    if (currentRange && decisiveDate && !isChangeOnNewEntity) {
        const rangeStart = getUtcDayjs(currentRange.DateValidFrom);
        const rangeEnd = currentRange.DateValidTo ? getUtcDayjs(currentRange.DateValidTo) : null;

        const isAfterStart = decisiveDate.isAfter(rangeStart, "day");
        const isBeforeEnd = !rangeEnd || decisiveDate.isBefore(rangeEnd, "day");

        if (isAfterStart && isBeforeEnd) {
            // shorten the existing current range to end the day before decisiveDate
            currentRange.DateValidTo = decisiveDate.toDate();

            // set defaultDate to first possible date, so new range starts there
            defaultDate = decisiveDate.add(1, "day").startOf("day");

            // nullify currentRange reference so execution flows into new range creation below
            currentRange = null;
        }
    }

    if (currentRange) {
        return { ranges, currentIndex: currentRangeIndex };
    }

    // create a new range
    const followingRange = ranges.find(r => defaultDate.isBefore(r.DateValidFrom, "day"));

    const newCurrentRange = createNewTemporalPropertyBagItem({
        // initialize with current range values if exists
        ...(dataToCopy ?? {}),
        DateValidFrom: ranges?.length ? defaultDate.toDate() : getDefaultTemporalDateValidFrom(dialogSettings, [], decisiveDate),
        DateValidTo: followingRange ? getUtcDayjs(followingRange.DateValidFrom).subtract(1, "day").toDate() : getDefaultTemporalDateValidTo(dialogSettings, [])
    }) as TTemporal;

    if (currentRange) {
        // we are creating a new range, shorten the current range by the new current
        currentRange.DateValidTo = getUtcDayjs(newCurrentRange.DateValidFrom).subtract(1, "day").toDate();
    }

    const sortedRanges = [...ranges, newCurrentRange]
        .sort((a, b) => a.DateValidFrom.getTime() - b.DateValidFrom.getTime());
    const currentIndex = sortedRanges.findIndex(r => r === newCurrentRange);
    return { ranges: sortedRanges, currentIndex };
};

export const getCurrentRangeIndex = (ranges: TTemporal[], date?: Date): number => {
    const today = getUtcDayjs(date);

    return ranges.findIndex(range => today.isSameOrAfter(range.DateValidFrom, "date") && today.isSameOrBefore(range.DateValidTo, "date"));
};

export function propageChangeToEveryTemporalRange(e: ISmartFieldChange, storage: FormStorage, temporalBagProperty: string = TemporalEntityProp.TemporalPropertyBag): void {
    const properties = getGroupingProperties(e.bindingContext, storage);
    const temporalBag = storage.getValueByPath(temporalBagProperty) as TTemporal[] ?? [];
    const groupedRanges = groupAndOptimizeRanges(temporalBag, properties);
    groupedRanges.forEach((range, idx) => {
        groupedRanges[idx] = setTemporalRangeNewValue(storage, e, range);
    });
    const ungroupedRanges = ungroupTemporalRanges(groupedRanges, temporalBag, properties);
    storage.setValueByPath(temporalBagProperty, ungroupedRanges);
}

export const updateTemporalRangesAfterCurrentTempPropChange = (e: ISmartFieldChange, storage: FormStorage, temporalBagProperty: string = TemporalEntityProp.TemporalPropertyBag): void => {
    const properties = getGroupingProperties(e.bindingContext, storage);
    const temporalBag = storage.getValueByPath(temporalBagProperty) as TTemporal[] ?? [];
    const groupedRanges = groupAndOptimizeRanges(temporalBag, properties);
    const dialogSettings = storage.getTemporalDialogSetting(e.bindingContext);
    const decisiveDate = getDecisiveDateForHistoryValues(storage, { dialogSettings });

    const isChangeOnNewEntity = (e.bindingContext.getParentCollection() ?? e.bindingContext.getRootParent()).isNew();
    const {
        ranges,
        currentIndex
    } = getOrPrepareNewCurrentRange(dialogSettings, groupedRanges, decisiveDate, isChangeOnNewEntity);
    ranges[currentIndex] = setTemporalRangeNewValue(storage, e, ranges[currentIndex]);

    const ungroupedRanges = ungroupTemporalRanges(ranges, temporalBag, properties);
    storage.setValueByPath(temporalBagProperty, ungroupedRanges);
};

export function isCurrentTemporalPropertyBagBc(bindingContext: BindingContext): boolean {
    return bindingContext.hasAnyParentPath(TemporalEntityProp.CurrentTemporalPropertyBag);
}

export const handleCurrentTemporalPropChange = (e: ISmartFieldChange, storage: FormStorage<any>, temporalBagProperty: string = TemporalEntityProp.TemporalPropertyBag): void => {
    if (!isCurrentTemporalPropertyBagBc(e.bindingContext)) {
        return;
    }

    updateTemporalRangesAfterCurrentTempPropChange(e, storage, temporalBagProperty);
};

export function setTemporalCurrentValue(storage: FormStorage, bindingContext: BindingContext, value: TValue, data?: IEntity): void {
    let additionalData: TRecordAny = null;
    const info = storage.getInfo(bindingContext);
    if (isEnum(info)) {
        additionalData = getEnumSelectItems(bindingContext.getEntityType().getName()).find(item => item.id === value)?.additionalData;
    }

    storage.setValue(bindingContext, value);
    const temporalBagBc = bindingContext.getParent().getParent().navigate(TemporalEntityProp.TemporalPropertyBag);
    const temporalBagPath = temporalBagBc.getNavigationPath();
    handleCurrentTemporalPropChange({
        bindingContext,
        value, additionalData
    }, storage, temporalBagPath);
}

export const setTemporalDefaultValueByPath = (storage: FormStorage, path: string): void => {
    const bindingContext = storage.data.bindingContext.navigate(path);
    const val = storage.getValue(bindingContext);
    if (val === undefined) {
        return;
    }
    const info = storage.getInfo(bindingContext);
    const defVal = getInfoValue(info, "defaultValue", {
        data: storage.data.entity,
        storage,
        info,
        bindingContext,
        context: storage.context
    });

    if (defVal !== undefined) {
        setTemporalCurrentValue(storage, bindingContext, defVal);
    }
};

export function setTemporalPropertyBagDefaultValues(storage: FormStorage): void {
    const { definition, bindingContext } = storage.data;
    if (bindingContext.isValidNavigation(TemporalEntityProp.TemporalPropertyBag)) {
        const ranges = (storage.getValueByPath(TemporalEntityProp.TemporalPropertyBag) ?? []) as TTemporal[];
        if (ranges.length) {
            return;
        }

        // sets current values, which starts "today"
        Object.keys(definition.fieldDefinition).forEach(key => {
            if (key.startsWith(TemporalEntityProp.CurrentTemporalPropertyBag) && isVisibleByPath(storage, key)) {
                setTemporalDefaultValueByPath(storage, key);
            }
        });
    }
}

export const EntityValueSourcePropNameSuffix = "Source";
export const EntityValueSourcePropNameEnumSuffix = `${EntityValueSourcePropNameSuffix}${ENUM_KEY_PROP_NAME}`;
export const EntityValueSourcePropNameSuffixes = [EntityValueSourcePropNameSuffix, EntityValueSourcePropNameEnumSuffix];

export function isFieldSynchronized({ storage, bindingContext }: IGetValueArgs): boolean {
    const propPath = bindingContext.getPath();
    const parentBc = bindingContext.getParent();
    if (parentBc.getPath(true) === TemporalEntityProp.CurrentTemporalPropertyBag) {
        const enumBc = parentBc.navigate(`${propPath}${EntityValueSourcePropNameEnumSuffix}`);
        const sourceCode = storage.getValue(enumBc) as PrEntityValueSourceCode;
        return isDefined(sourceCode) && sourceCode !== PrEntityValueSourceCode.Entity;
    }
    if (parentBc.isValidNavigation(TemporalEntityProp.Default)) {
        const valueBc = parentBc.navigate(TemporalEntityProp.Default);
        const value = storage.getValue(valueBc) as TRecordAny;
        return !isObjectEmpty(value);
    }
    return false;
}

interface IGetTemporalPropertyFieldDefinitionArgs {
    propName: string;
    // definition that will be applied to both TemporalPropertyBag and CurrentTemporalPropertyBag
    definition?: TFieldDefinition;
    // definition that will be applied only to the CurrentTemporalPropertyBag field shown in form
    currentDefinition?: TFieldDefinition;
    withOpeningButton?: boolean;
    isSynchronized?: boolean;
}

export interface IGetTemporalPropertyLineItemFieldDefinitionArgs extends IGetTemporalPropertyFieldDefinitionArgs {
    collectionName: string;
}

const isCurrentTemporalPropertyBagFieldDisabled: TGetValueFn<boolean> = (args: IGetValueArgs) => {
    const currentBagBc = args.bindingContext.getParent();
    const storage = args.storage as TTemporalFormStorage;
    const currentBag = storage.getValue(currentBagBc);
    const decisiveDate = getDecisiveDateForHistoryValues(storage, { bindingContext: args.bindingContext });
    const now = getUtcDayjs();
    if (decisiveDate && now.isBefore(decisiveDate)) {
        // in case current date is before decisive date, we don't allow editting current value,
        // because current value is already in the past and should not be changed, only future values can be changed.
        //  => it must be changed via the temporal property dialog, because we cannot change different value than the one displayed in it.
        // alternatively we could consider redefining the current value to display the one according to decisive date, but that
        // would be confusing for the user as different field may display value from different time period??
        return true;
    }
    if (!currentBag?.DateValidFrom || !currentBag?.DateValidTo) {
        return false;
    }
    return !now.isBetween(currentBag.DateValidFrom, currentBag.DateValidTo, "day", "[]");
};

export function getTemporalPropertyLineItemFieldDefinition(args: IGetTemporalPropertyLineItemFieldDefinitionArgs): TFieldsDefinition {
    const commonDef: TFieldDefinition = {
        ...(args.definition ?? {})
    };
    const def: TFieldDefinition = {
        ...commonDef,
        customizationData: {
            useForCustomization: false
        }
    };
    const currentDef: TFieldDefinition = {
        ...commonDef,
        ...(args.currentDefinition ?? {}),
        extraFieldContentAfter: args.currentDefinition?.extraFieldContentAfter !== undefined ? args.currentDefinition?.extraFieldContentAfter : (args.withOpeningButton === true ? getTemporalPropertyExtraFieldContentAfter() : null)
    };

    const currentPropertyBagPath = createPath(args.collectionName, TemporalEntityProp.CurrentTemporalPropertyBag, args.propName);

    const additionalProperties = currentDef.additionalProperties ?? [];
    const fieldSettings: IFieldDef["fieldSettings"] = {
        ...(currentDef.fieldSettings ?? {})
    };

    if (args.isSynchronized) {
        additionalProperties.push({ id: `/${currentPropertyBagPath}${EntityValueSourcePropNameEnumSuffix}` });
        fieldSettings.isSynchronized = isFieldSynchronized;
    }

    additionalProperties.push(
        { id: `/${createPath(args.collectionName, TemporalEntityProp.CurrentTemporalPropertyBag)}/DateValidFrom` },
        { id: `/${createPath(args.collectionName, TemporalEntityProp.CurrentTemporalPropertyBag)}/DateValidTo` }
    );

    // add disabled condition for history / future values
    currentDef.isDisabled = currentDef.isDisabled ? ifAny(currentDef.isDisabled, isCurrentTemporalPropertyBagFieldDisabled) : isCurrentTemporalPropertyBagFieldDisabled;

    const tmpFieldsDef: TFieldsDefinition = {};

    // todo verify that this condition make sense,
    // but i believe it does, because we want to add TemporalValidityFieldsDef only for the collection
    // and the default TemporalValidityFieldsDef should be manually add in each definition once, if it uses tmp fields.
    // otherwise, this would override the TemporalValidityFieldsDef with each get tmp def field call
    if (args.collectionName) {
        forEachKey(TemporalValidityFieldsDef, (key) => {
            const collectionKey = createPath(args.collectionName, key);
            tmpFieldsDef[collectionKey] = TemporalValidityFieldsDef[key];
        });
    }

    return {
        [createPath(args.collectionName, TemporalEntityProp.TemporalPropertyBag, args.propName)]: {
            ...def,
            isReadOnly: def.isReadOnly ? ifAny(def.isReadOnly, isTemporalFieldReadOnly) : isTemporalFieldReadOnly
        },
        [currentPropertyBagPath]: {
            ...currentDef,
            fieldSettings,
            additionalProperties
        },
        ...tmpFieldsDef
    };
}

export function getTemporalPropertyFieldDefinition(args: IGetTemporalPropertyFieldDefinitionArgs): TFieldsDefinition {
    const currentDefinition = {
        ...(args.currentDefinition ?? {})
    };

    return getTemporalPropertyLineItemFieldDefinition({
        ...args,
        collectionName: null,
        currentDefinition,
        withOpeningButton: args.withOpeningButton !== false
    });
}

interface IAddEmptyTemporalPropertyBagItemArgs {
    storage?: FormStorage;
    bindingContext?: BindingContext;
    tempSettings?: ITemporalPropertySettings["temporalDialog"];
}

// counter for new item ids
export const newTemporalRangeId = {
    _counter: 0,
    get: function (): number {
        this._counter += 1;
        return this._counter;
    }
};

/**
 * Creates new temporal property bag item with default values,
 * @param values
 * @param args
 */
export function createNewTemporalPropertyBagItem<T extends TTemporal = TTemporal>(values: Partial<T>, args: IAddEmptyTemporalPropertyBagItemArgs = {}): T {
    const { storage, bindingContext, tempSettings } = args;
    const info = storage && bindingContext ? storage.getInfo(bindingContext) : null;
    const settings = tempSettings ?? info?.fieldSettings?.temporalDialog;

    const items = storage && bindingContext ? storage.getValue(bindingContext.getParent()) as TTemporal[] : [];

    const defaultValues: Partial<T> = {};
    // if (bindingContext && storage && settings) {
    //     const bagBc = bindingContext.getParent();
    //
    //     for (const column of settings?.columns) {
    //         const columnBc = bagBc.navigate(column);
    //         const info = storage.getInfo(columnBc);
    //
    //         if (info && info.defaultValue) {
    //             const defVal = getInfoValue(info, "defaultValue", {
    //                 data: storage.data.entity,
    //                 storage,
    //                 info,
    //                 bindingContext: columnBc,
    //                 context: storage.context
    //             });
    //
    //             updateDataOnChange({
    //                 storage,
    //                 bindingContext: columnBc,
    //                 dataBindingContext: bagBc,
    //                 data: defaultValues,
    //                 newValue: defVal,
    //                 preventCloning: true
    //             }, true);
    //
    //             correctEnumValue(columnBc, defVal, { data: defaultValues, dataBindingContext: bagBc });
    //         }
    //     }
    // }

    // default dateValidFrom & dateValidTo if not in the given values
    if (info || settings) {
        const decisiveDate = getDecisiveDateForHistoryValues(storage, { dialogSettings: settings });
        // todo: withoutGaps is TInfoValue<boolean>, but I'am not able to always get binding context here, so fallback to settings.withoutGaps right now as it's mostly used as boolean
        const withoutGaps = isDefined(bindingContext) ? getInfoValue(settings, "withoutGaps", {
            storage,
            bindingContext
        }) : !!settings.withoutGaps;
        defaultValues.DateValidFrom = getDefaultTemporalDateValidFrom(settings, items, decisiveDate, withoutGaps);
        defaultValues.DateValidTo = getDefaultTemporalDateValidTo(settings, items);
    }

    // create new entity with default values
    return BindingContext.createNewEntity(newTemporalRangeId.get(), {
        ...defaultValues,
        ...values
    }) as T;
}

export function createNewTemporalLineItem<T extends IEntity = IEntity, TemporalT = TTemporal>(temporalSettings: TTemporalDialogSettings, values: T, range?: TTemporal<TemporalT>): T {
    const newRange = createNewTemporalPropertyBagItem(range, { tempSettings: temporalSettings });
    const { DateValidFrom, DateValidTo } = newRange;
    return {
        ...values,
        DateValidFrom, DateValidTo,
        TemporalPropertyBag: [newRange]
    } as T;
}

/**
 * Enhances new line item entity with temporal settings, changes the entity given in params.
 * @param temporalSettings
 * @param item
 * @param range
 */
export function enhanceNewTemporalLineItem<T extends TTemporalLineItem = TTemporalLineItem, TemporalT = TTemporal>(storage: FormStorage, temporalSettings: TTemporalDialogSettings, item: T, range?: TTemporal<TemporalT>): void {
    const newRange = createNewTemporalPropertyBagItem({
        ...range,
        ...item[TemporalEntityProp.CurrentTemporalPropertyBag],
    }, { tempSettings: temporalSettings, storage });

    if (!temporalSettings.isLineItemWithoutValidityProps) {
        const { DateValidFrom, DateValidTo } = newRange;
        item.DateValidFrom = DateValidFrom;
        item.DateValidTo = DateValidTo;
    }
    item.TemporalPropertyBag = [newRange];
}
    
export function handleTemporalLineItemsActions<T extends TTemporalLineItem = TTemporalLineItem>(storage: FormStorage<any>, args: ISmartFastEntriesActionEvent<T>): void {

    const _defaultBehavior = () => {
        storage.handleLineItemsAction(args);
        storage.refresh();
    };

    switch (args.actionType) {
        case ActionType.Add:
            // new item has to be enhanced with temporal property bag and other props
            const { definition } = storage.data;
            const groupDef = definition.groups.find(group => group.id === args.groupId);
            const [item] = args.affectedItems;

            if (!isObjectEmpty(groupDef.lineItems?.temporalDialog)) {
                enhanceNewTemporalLineItem(storage, groupDef.lineItems.temporalDialog, item);
            }

            _defaultBehavior();
            break;

        case ActionType.Remove:
            // todo: if line item is not fully editable, just set the DateValidTo to last editable date
            _defaultBehavior();
            break;

        case ActionType.Custom:
        case ActionType.Clone:
            // clone actions are not defined for temporal line items, customAction should be handled in caller
            throw new Error(`ActionType '${args.actionType}' not handled in common method`);

        default:
            _defaultBehavior();
    }
}

/**
 * Function receives temporal property bag item and if it's an empty range (means it's synced from another entity),
 * it replaces it with history values from the given time period.
 * @param value
 * @param historyData
 * @param sourceCode
 */
export function replaceSyncedValueWithHistoryValues<T extends Partial<TTemporal<IEntity>>>(value: T, historyData: T[], sourceCode: PrEntityValueSourceCode = PrEntityValueSourceCode.Default): T[] {
    if (!isEmptyRange(value)) {
        return [value];
    }
    const newRanges: T[] = [];

    const fromDayjs = getUtcDayjs(value.DateValidFrom);
    const toDayjs = getUtcDayjs(value.DateValidTo);

    historyData?.forEach((range, idx) => {
        // if value range overlaps with history data (at least partly)
        if (fromDayjs.isBefore(range.DateValidTo) && toDayjs.isAfter(range.DateValidFrom)) {
            newRanges.push(createNewTemporalPropertyBagItem({
                ...range,
                DateValidFrom: fromDayjs.isBefore(range.DateValidFrom) ? range.DateValidFrom : value.DateValidFrom,
                DateValidTo: toDayjs.isAfter(range.DateValidTo) ? range.DateValidTo : value.DateValidTo,
                // range might be already expanded from TemplateDefault, so keep the original source
                [SourceCodeLocalPath]: range.SourceCode ?? sourceCode
            }) as T);
        }
    });

    return newRanges;
}

export function markHistoryValuesNotSynced<T extends Partial<TTemporal<IEntity>>>(temporalPropertyBag: T[]): T[] {
    return temporalPropertyBag.map(item => {
        if (item[SourceCodeLocalPath]) {
            const { [SourceCodeLocalPath]: _, ...restProps } = item;
            // just remove the SourceCodeLocalPath, so the item is not considered as synced anymore
            // all remaining values are kept as is and will be used locally
            return {
                ...restProps,
                [IsItemWithRemovedSyncPath]: true // mark the entity so we can handle it same way as new item eg. for validation
            } as unknown as T;
        }
        return item;
    });
}

export async function getSyncedLineItemHistoryValues(storage: FormStorage, component: IEntity, data: TTemporal[]): Promise<TTemporal[]> {
    if (!data) {
        data = [];
    }

    const defaultEntity = component.Default;

    if (!defaultEntity) {
        // there are no default data, just return actual TemporalPropertyBag
        return data;
    }

    const historyData = defaultEntity.TemporalPropertyBag;

    return data.reduce((res: TTemporal[], item: TTemporal) =>
        res.concat(replaceSyncedValueWithHistoryValues(item, historyData, PrEntityValueSourceCode.Default)), []);
}

export function getTemporalDataColumns(columns: string[], withDateColumns = true): string[] {
    const dataColumns = columns.filter(path => !BindingContext.isLocalContextPath(path));
    return withDateColumns ? [...dataColumns, TemporalEntityEntity.DateValidFrom, TemporalEntityEntity.DateValidTo] : dataColumns;
}

export function getCurrentBagFromUngrouped(ungrouped: TTemporal[]): TTemporal {
    const today = getUtcDayjs();
    let current = ungrouped.find((bag: TTemporal) => isCurrentBag(today, bag));
    if (!current) {
        // in case there is no current item, we do a fallback to future one
        current = ungrouped.find((bag: TTemporal) => isFutureBag(today, bag));
    }
    if (!current) {
        // if there is not current or future, we use the latest history item
        current = ungrouped.reverse().find((bag: TTemporal) => isHistoryBag(today, bag));
    }

    return current;
}

export function expandCurrentTemporalBagFromDefault(storage: FormStorage, path: string): void {
    const lineItems: TTemporalLineItem[] = storage.getValueByPath(path) || [];
    lineItems.forEach((lineItem: TTemporalLineItem) => {
        // split CurrentTemporalPropertyBag to properties ends with "SourceCode" and the others
        const SourceCodes: IEntity = {};
        const rangeProperties: IEntity = {};

        if (isObjectEmpty(lineItem.CurrentTemporalPropertyBag)) {
            lineItem.CurrentTemporalPropertyBag = getCurrentBagFromUngrouped(lineItem.TemporalPropertyBag);
        }

        Object.keys(lineItem.CurrentTemporalPropertyBag).forEach(key => {
            if (EntityValueSourcePropNameSuffixes.some(suffix => key.endsWith(suffix))) {
                // skip source code properties
                SourceCodes[key] = lineItem.CurrentTemporalPropertyBag[key];
            } else {
                rangeProperties[key] = lineItem.CurrentTemporalPropertyBag[key];
            }
        });
        if (isEmptyRange(rangeProperties) && lineItem?.Default) {
            lineItem.CurrentTemporalPropertyBag = {
                ...lineItem.Default.CurrentTemporalPropertyBag,
                ...SourceCodes
            };
        }
    });
}

export function getFakeTemporalFieldFormatter(path: string): TFormatterFn {
    return (val, args) => {
        const { bindingContext, storage } = args;
        const pathArr = path.split(SEPARATOR);
        const propName = pathArr.pop();
        const entityBc = bindingContext.getParent().getParent();
        const entity = pathArr.length > 0 ? storage.getValue(entityBc.navigate(pathArr.join(SEPARATOR))) : storage.getValue(entityBc);
        const propBc = entityBc.navigate(path);
        if (entity && propBc.isEnum()) {
            const key = propBc.getKeyPropertyName();
            const value = entity[`${propName}${key}`] ?? entity[propName]?.[key];
            return getEnumDisplayValue(propBc.getEntityType().getName(), value);
        }
        return entity?.[propName];
    };
}